<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import { BRANDS } from "@/brand";
import {
  formatMigrationDeadline,
  migrationUrl,
  mustCopyMigrationUrl,
  openMigrationUrl,
} from "@/brand/migration";
import { trackAppFeature } from "@/analytics/app-analytics";
import { flushEventQueue } from "@/analytics/event-queue";
import { useScreen } from "@/composable/useResponsive";
import DRBtn from "@/components/UI/DRBtn.vue";
import BrandMigrationCopyButton from "./BrandMigrationCopyButton.vue";
import BrandMigrationStep from "./BrandMigrationStep.vue";
import BrandMigrationSyncStatus from "./BrandMigrationSyncStatus.vue";

const showModal = defineModel<boolean>({ required: true });

const { t, i18next } = useTranslation();
const { screen } = useScreen();

const deadline = computed(() => formatMigrationDeadline(i18next.language));

/** L'adresse telle qu'on la lit : « app.inspexio.com », sans le protocole. */
const migrationHost = new URL(migrationUrl).host;

const copyMode = mustCopyMigrationUrl();

const appIcon = (brand: keyof typeof BRANDS) =>
  BRANDS[brand].icons.find((icon) => icon.sizes === "192x192")?.src ??
  BRANDS[brand].icons[0].src;

const closeModal = () => {
  showModal.value = false;
};

const trackRedirect = (via: "open" | "copy") => {
  trackAppFeature("brand_migration_redirect_clicked", { via });
  void flushEventQueue();
};

const openInspexio = () => {
  trackRedirect("open");
  openMigrationUrl();
};
</script>

<template>
  <el-dialog
    v-model="showModal"
    :title="t('brandMigration.title')"
    :width="screen.gt.sm ? '560px' : '100%'"
    append-to-body
    align-center
  >
    <div class="d-flex flex-column gap-4">
      <div class="d-flex align-items-center justify-content-center gap-4">
        <img
          :src="appIcon('dr')"
          :alt="BRANDS.dr.name"
          class="app-icon app-icon--legacy"
        />
        <i class="bi-arrow-right fs-2 text-body-secondary" aria-hidden="true" />
        <img
          :src="appIcon('inspexio')"
          :alt="BRANDS.inspexio.name"
          class="app-icon"
        />
      </div>

      <p class="text-center mb-0">{{ t("brandMigration.intro") }}</p>

      <ol class="list-unstyled d-flex flex-column gap-3 mb-0">
        <BrandMigrationStep :step="1" :title="t('brandMigration.step1Title')">
          <BrandMigrationSyncStatus @navigate="closeModal" />
        </BrandMigrationStep>
        <BrandMigrationStep :step="2" :text="t('brandMigration.step2Text')">
          <template #title>
            {{ t("brandMigration.step2TitleBefore") }}
            <span v-if="copyMode" class="text-primary">{{
              migrationHost
            }}</span>
            <a v-else :href="migrationUrl" @click.prevent="openInspexio">{{
              migrationHost
            }}</a>
            {{ t("brandMigration.step2TitleAfter") }}
          </template>
        </BrandMigrationStep>
        <BrandMigrationStep
          :step="3"
          :title="t('brandMigration.step3Title')"
          :text="t('brandMigration.step3Text')"
        />
      </ol>

      <div class="notice d-flex flex-column gap-1">
        <strong v-if="copyMode">
          <i class="bi-globe me-1" aria-hidden="true" />
          {{ t("brandMigration.copyInstruction", { host: migrationHost }) }}
        </strong>
        <span>
          <i class="bi-phone me-1" aria-hidden="true" />
          {{ t("brandMigration.devices") }}
        </span>
        <strong v-if="deadline">
          <i class="bi-calendar-event me-1" aria-hidden="true" />
          {{ t("brandMigration.deadline", { date: deadline }) }}
        </strong>
      </div>
    </div>

    <template #footer>
      <div class="d-flex justify-content-end gap-2">
        <DRBtn
          :label="t('brandMigration.later')"
          variant="outline"
          @click="closeModal"
        />
        <BrandMigrationCopyButton
          v-if="copyMode"
          :url="migrationUrl"
          @copied="trackRedirect('copy')"
        />
        <DRBtn
          v-else
          :label="t('brandMigration.cta')"
          rightIcon="bi-box-arrow-up-right"
          variant="primary"
          @click="openInspexio"
        />
      </div>
    </template>
  </el-dialog>
</template>

<style lang="scss" scoped>
.app-icon {
  width: 72px;
  height: 72px;
  border-radius: 16px;
  border: 1px solid var(--bs-border-color);

  &--legacy {
    opacity: 0.6;
  }
}

.notice {
  padding: 0.75rem;
  border-radius: 0.375rem;
  background-color: var(--bs-tertiary-bg);
  font-size: 0.875rem;
}
</style>
